import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { API_BASE_URL } from "../helpers/constants";
import { getMatchScore } from "./accountDetail/accountDetailUtils";
import AccountDetailHeader from "./accountDetail/AccountDetailHeader";
import AccountComparisonBar from "./accountDetail/AccountComparisonBar";
import AccountPortfolioSetup from "./accountDetail/AccountPortfolioSetup";
import AccountClaimsAnalysis, {
  CLAIM_COMPARISON_BATCH_SIZE,
} from "./accountDetail/AccountClaimsAnalysis";
import AccountPatentReader from "./accountDetail/AccountPatentReader";

function AccountDetail() {
  const navigate = useNavigate();
  const { accountId } = useParams();
  const { token, user, logout } = useAuth();

  // Account state
  const [account, setAccount] = useState(null);

  // Portfolio state
  const [defaultPortfolio, setDefaultPortfolio] = useState(null);
  const [competitorPortfolios, setCompetitorPortfolios] = useState([]);
  const [selectedCompPortfolioId, setSelectedCompPortfolioId] = useState("");

  // Patent state for each portfolio
  const [defaultPortfolioPatents, setDefaultPortfolioPatents] = useState([]);
  const [competitorPortfolioPatents, setCompetitorPortfolioPatents] = useState(
    [],
  );
  const [defaultProcessingPatents, setDefaultProcessingPatents] = useState([]);
  const [competitorProcessingPatents, setCompetitorProcessingPatents] = useState(
    [],
  );

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Add patent state
  const [newMyPatentId, setNewMyPatentId] = useState("");
  const [newCompPatentId, setNewCompPatentId] = useState("");

  // Detections state (claims analysis)
  const [detections, setDetections] = useState([]);
  const [loadingDetections, setLoadingDetections] = useState(false);
  const [expandedDetectionId, setExpandedDetectionId] = useState(null);
  const [detectionDetails, setDetectionDetails] = useState({});
  const [patentClaimsByPatentUuid, setPatentClaimsByPatentUuid] = useState({});
  const [activeTab, setActiveTab] = useState("claims");
  const claimsAnalysisScrollRef = useRef(null);
  const claimsScrollTopRestoreRef = useRef(0);
  const [claimsCollapsedComparisonKeys, setClaimsCollapsedComparisonKeys] =
    useState(() => new Set());
  const [claimsComparisonVisibleCount, setClaimsComparisonVisibleCount] =
    useState(CLAIM_COMPARISON_BATCH_SIZE);
  const [readerLoading, setReaderLoading] = useState(false);
  const [readerError, setReaderError] = useState(null);
  const [selectedReaderPatent, setSelectedReaderPatent] = useState(null);
  const [readerSourceLabel, setReaderSourceLabel] = useState("");
  const [highlightedReaderClaimNumber, setHighlightedReaderClaimNumber] =
    useState(null);

  const expandedClaimsTabClaimCount =
    expandedDetectionId != null
      ? detectionDetails[expandedDetectionId]?.claim_detections?.length ?? 0
      : 0;

  // Fetch account and portfolios on mount
  useEffect(() => {
    if (accountId && user?.user_id && token) {
      fetchAccount();
      fetchPortfolios();
    }
  }, [accountId, user, token]);

  // Fetch patents when default portfolio changes
  useEffect(() => {
    if (defaultPortfolio?.portfolio_id) {
      fetchPortfolioPatents(defaultPortfolio.portfolio_id, true);
      fetchProcessingPatents(defaultPortfolio.portfolio_id, true);
    }
  }, [defaultPortfolio?.portfolio_id]);

  useEffect(() => {
    const savedPortfolioId = localStorage.getItem("selectedCompPortfolioId");
    if (
      savedPortfolioId &&
      competitorPortfolios.some((p) => p.portfolio_id === savedPortfolioId)
    ) {
      setSelectedCompPortfolioId(savedPortfolioId);
    }
  }, [competitorPortfolios]);

  useEffect(() => {
    if (!defaultPortfolio?.portfolio_id) return;
    const timer = setInterval(() => {
      fetchProcessingPatents(defaultPortfolio.portfolio_id, true);
    }, 5000);
    return () => clearInterval(timer);
  }, [defaultPortfolio?.portfolio_id, token, accountId]);

  useEffect(() => {
    if (!selectedCompPortfolioId) {
      setCompetitorPortfolioPatents([]);
      setCompetitorProcessingPatents([]);
      setDetections([]);
      return;
    }

    localStorage.setItem("selectedCompPortfolioId", selectedCompPortfolioId);

    fetchPortfolioPatents(selectedCompPortfolioId, false);
    fetchProcessingPatents(selectedCompPortfolioId, false);
  }, [selectedCompPortfolioId]);

  useEffect(() => {
    if (!selectedCompPortfolioId) return;
    const timer = setInterval(() => {
      fetchProcessingPatents(selectedCompPortfolioId, false);
    }, 5000);
    return () => clearInterval(timer);
  }, [selectedCompPortfolioId, token, accountId]);

  useEffect(() => {
    if (
      !defaultPortfolio?.portfolio_id ||
      !selectedCompPortfolioId ||
      defaultPortfolioPatents.length === 0 ||
      competitorPortfolioPatents.length === 0
    ) {
      setDetections([]);
      return;
    }

    fetchDetections(defaultPortfolio.portfolio_id, selectedCompPortfolioId);
  }, [
    defaultPortfolio?.portfolio_id,
    selectedCompPortfolioId,
    defaultPortfolioPatents,
    competitorPortfolioPatents,
  ]);

  useEffect(() => {
    setClaimsComparisonVisibleCount(CLAIM_COMPARISON_BATCH_SIZE);
    setClaimsCollapsedComparisonKeys(new Set());
  }, [expandedDetectionId]);

  useEffect(() => {
    setClaimsComparisonVisibleCount((v) =>
      expandedClaimsTabClaimCount === 0
        ? CLAIM_COMPARISON_BATCH_SIZE
        : Math.min(v, expandedClaimsTabClaimCount),
    );
  }, [expandedClaimsTabClaimCount]);

  // Fetch account details
  const fetchAccount = async () => {
    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`${API_BASE_URL}/v1/account/${accountId}`, {
        headers: {
          "Content-Type": "application/json",
          "X-User-Id": user.user_id,
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Failed to fetch account");
      }

      setAccount(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch all portfolios for the account. Resolves to { compPorts, defaultPort } for callers
  // that need IDs immediately after await (e.g. select newly created portfolio).
  const fetchPortfolios = async () => {
    setLoading(true);
    setError(null);

    let result = { compPorts: [], defaultPort: null };
    try {
      const res = await fetch(`${API_BASE_URL}/v1/portfolio`, {
        headers: {
          "Content-Type": "application/json",
          "X-Account-Id": accountId,
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Failed to fetch portfolios");
      }

      const portfolioList = data.portfolios || [];

      // Sort by type: default portfolio vs competitor portfolios
      const defaultPort = portfolioList.find(
        (p) => p.portfolio_type === "default",
      );
      const compPorts = portfolioList.filter(
        (p) => p.portfolio_type !== "default",
      );

      setDefaultPortfolio(defaultPort);
      setCompetitorPortfolios(compPorts);
      result = { compPorts, defaultPort };

      // Set first competitor as selected if available
      if (compPorts.length > 0 && !selectedCompPortfolioId) {
        setSelectedCompPortfolioId(compPorts[0].portfolio_id);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
    return result;
  };

  // Fetch patents for a specific portfolio
  const fetchPortfolioPatents = async (portfolioId, isDefault) => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/v1/portfolio/${portfolioId}/patent`,
        {
          headers: {
            "Content-Type": "application/json",
            "X-Account-Id": accountId,
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Failed to fetch patents");
      }

      const patents = (data.patents || []).map((patent) => ({
        patentUuid: patent.patent_id,
        patent_id: patent.application_number ?? null,
        patent_name: patent.patent_name ?? null,
        processing:
          patent.processing === true ||
          patent.status === "processing" ||
          patent.patent_status === "processing",
      }));

      if (isDefault) {
        setDefaultPortfolioPatents(patents);
      } else {
        setCompetitorPortfolioPatents(patents);
      }
      return patents;
    } catch (err) {
      console.error("Error fetching portfolio patents:", err);
      // Don't set error for patents, just log it
      return null;
    }
  };

  const fetchAllProcessingPatents = async (
    portfolioId,
    accumulatedPatents = [],
    nextToken = null,
  ) => {
    const queryParams = new URLSearchParams();
    queryParams.append("maxResults", "50");
    if (nextToken) queryParams.append("nextToken", nextToken);

    const res = await fetch(
      `${API_BASE_URL}/v1/portfolio/${portfolioId}/processing?${queryParams.toString()}`,
      {
        headers: {
          "Content-Type": "application/json",
          "X-Account-Id": accountId,
          Authorization: `Bearer ${token}`,
        },
      },
    );

    const data = await res.json();
    if (!res.ok) {
      throw new Error(data.message || "Failed to fetch processing patents");
    }

    const currentBatch = (data.patents || []).map((patent) => ({
      patent_id: patent.application_number ?? "",
      patent_name: null,
      processing: true,
    }));
    const allPatents = [...accumulatedPatents, ...currentBatch];

    if (data.next_token) {
      return fetchAllProcessingPatents(portfolioId, allPatents, data.next_token);
    }
    if (data.nextToken) {
      return fetchAllProcessingPatents(portfolioId, allPatents, data.nextToken);
    }
    return allPatents;
  };

  const processingDocKeySet = (list) =>
    new Set(
      (list || [])
        .map((p) => String(p.patent_id ?? "").trim())
        .filter(Boolean),
    );

  const fetchProcessingPatents = async (portfolioId, isDefault) => {
    try {
      const patents = await fetchAllProcessingPatents(portfolioId);
      const nextKeys = processingDocKeySet(patents);

      const mergeAndMaybeRefreshFullList = (prev) => {
        const prevKeys = processingDocKeySet(prev);
        const somethingLeftQueue =
          prevKeys.size > 0 && [...prevKeys].some((k) => !nextKeys.has(k));
        if (somethingLeftQueue) {
          queueMicrotask(() => fetchPortfolioPatents(portfolioId, isDefault));
        }
        return patents;
      };

      if (isDefault) {
        setDefaultProcessingPatents(mergeAndMaybeRefreshFullList);
      } else {
        setCompetitorProcessingPatents(mergeAndMaybeRefreshFullList);
      }
    } catch (err) {
      console.error("Error fetching processing patents:", err);
    }
  };

  // Recursive function to fetch all detections by following pagination tokens
  const fetchAllDetections = async (
    competitorPortfolioId,
    accumulatedDetections = [],
    nextToken = null,
  ) => {
    if (!competitorPortfolioId) {
      return [];
    }

    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      queryParams.append("maxResults", "250");
      if (nextToken) {
        queryParams.append("nextToken", nextToken);
      }

      const url = `${API_BASE_URL}/v1/portfolio/${competitorPortfolioId}/detection?${queryParams.toString()}`;

      const res = await fetch(url, {
        headers: {
          "Content-Type": "application/json",
          "X-Account-Id": accountId,
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Failed to fetch detections");
      }

      // Transform the current batch of detections
      const currentBatch = (data.detections || []).map((detection) => ({
        id: detection.detection_id,
        detectionId: detection.detection_id,
        myPatentId: detection.conflicting_application_number,
        myPatentUuid: detection.conflicting_patent_id,
        compPatentId: detection.application_number,
        compPatentUuid: detection.patent_id,
        similarity: getMatchScore(detection.priority),
        dismissed: detection.dismissed,
        portfolioId: detection.portfolio_id,
        conflictingPortfolioId: detection.conflicting_portfolio_id,
      }));

      // Combine with accumulated detections
      const allDetections = [...accumulatedDetections, ...currentBatch];

      // If there's a nextToken, recursively fetch more
      if (data.nextToken) {
        return await fetchAllDetections(
          competitorPortfolioId,
          allDetections,
          data.nextToken,
        );
      }

      // No more pages, return all accumulated detections
      return allDetections;
    } catch (err) {
      console.error("Error fetching detections:", err);
      throw err;
    }
  };

  // Updated fetchDetections function to use the recursive fetcher
  const fetchDetections = async (myPortfolioId, competitorPortfolioId) => {
    if (!myPortfolioId || !competitorPortfolioId) {
      return;
    }

    setLoadingDetections(true);

    try {
      // Fetch ALL detections across all pages
      const allDetections = await fetchAllDetections(competitorPortfolioId);

      // Get the current patent IDs from both portfolios
      const myPatentIds = new Set(
        defaultPortfolioPatents.map((p) => p.patent_id),
      );
      const compPatentIds = new Set(
        competitorPortfolioPatents.map((p) => p.patent_id),
      );

      // Filter detections to only show relevant ones
      const filteredDetections = allDetections.filter((d) => {
        const hasMyPatent = myPatentIds.has(d.myPatentId);
        const hasCompPatent = compPatentIds.has(d.compPatentId);

        return hasMyPatent && hasCompPatent;
      });

      setDetections(filteredDetections);
    } catch (err) {
      console.error("Error fetching detections:", err);
      // Clear detections on error
      setDetections([]);
    } finally {
      setLoadingDetections(false);
    }
  };

  // Fetch detailed claim comparisons for a specific detection
  const fetchDetectionDetails = async (detection) => {
    if (detectionDetails[detection.detectionId]) {
      // Already loaded
      return;
    }

    try {
      const res = await fetch(
        `${API_BASE_URL}/v1/portfolio/${detection.portfolioId}/detection/${detection.detectionId}`,
        {
          headers: {
            "Content-Type": "application/json",
            "X-Account-Id": accountId,
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Failed to fetch detection details");
      }

      setDetectionDetails((prev) => ({
        ...prev,
        [detection.detectionId]: data,
      }));
    } catch (err) {
      console.error("Error fetching detection details:", err);
      setError(`Failed to fetch claim details: ${err.message}`);
    }
  };

  const getPortfolioPatentDisplayName = (patentUuid) => {
    const row =
      defaultPortfolioPatents.find((p) => p.patentUuid === patentUuid) ||
      competitorPortfolioPatents.find((p) => p.patentUuid === patentUuid);
    const raw = row?.patent_name;
    if (raw == null) return null;
    const t = String(raw).trim();
    return t === "" ? null : t;
  };

  const fetchPatentClaimsForPopup = async (portfolioId, patentUuid) => {
    if (!portfolioId || !patentUuid) return;
    if (patentClaimsByPatentUuid[patentUuid]) return;

    try {
      const res = await fetch(
        `${API_BASE_URL}/v1/portfolio/${portfolioId}/patent/${patentUuid}`,
        {
          headers: {
            "Content-Type": "application/json",
            "X-Account-Id": accountId,
            Authorization: `Bearer ${token}`,
          },
        },
      );
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || "Failed to fetch patent claims");
      }

      const claimsByNumber = {};
      (data.claims || []).forEach((claim) => {
        if (claim?.claim_number != null) {
          claimsByNumber[String(claim.claim_number)] = claim.claim;
        }
      });

      setPatentClaimsByPatentUuid((prev) => ({
        ...prev,
        [patentUuid]: {
          patentDocumentNumber:
            data.application_number || data.patent_id || patentUuid,
          patentDisplayName: getPortfolioPatentDisplayName(patentUuid),
          claimsByNumber,
        },
      }));
    } catch (err) {
      console.error("Error fetching patent claims for popup:", err);
    }
  };

  // Toggle detection expansion
  const toggleDetection = async (detection) => {
    if (expandedDetectionId === detection.detectionId) {
      setExpandedDetectionId(null);
    } else {
      setExpandedDetectionId(detection.detectionId);
      await fetchDetectionDetails(detection);
      fetchPatentClaimsForPopup(
        detection.conflictingPortfolioId,
        detection.myPatentUuid,
      );
      fetchPatentClaimsForPopup(detection.portfolioId, detection.compPatentUuid);
    }
  };

  // Add patent to a portfolio
  const addPatentToPortfolio = async (portfolioId, patentId) => {
    if (!patentId.trim()) {
      setError("Please enter a patent ID");
      setTimeout(() => setError(null), 3000);
      return;
    }

    try {
      const res = await fetch(
        `${API_BASE_URL}/v1/portfolio/${portfolioId}/patent`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Account-Id": accountId,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ application_number: patentId }),
        },
      );

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Failed to add patent");
      }

      // Clear the input immediately
      const isDefault = portfolioId === defaultPortfolio?.portfolio_id;
      if (isDefault) {
        setNewMyPatentId("");
      } else {
        setNewCompPatentId("");
      }
      await fetchProcessingPatents(portfolioId, isDefault);

      // Clear any existing errors
      setError(null);

      // Show temporary success notification
      setSuccessMessage(
        `Patent ${patentId} queued for processing. It will appear shortly.`,
      );
      setTimeout(() => setSuccessMessage(null), 5000);

      // Poll for the patent to appear (check every 2 seconds for up to 30 seconds)
      let pollCount = 0;
      const maxPolls = 15;
      const pollInterval = setInterval(async () => {
        pollCount++;

        // Refresh the patent list
        await fetchPortfolioPatents(portfolioId, isDefault);

        // Check if the patent now appears in the list
        const currentPatents = isDefault
          ? defaultPortfolioPatents
          : competitorPortfolioPatents;
        const patentExists = currentPatents.some(
          (p) => p.patent_id === patentId,
        );

        if (patentExists || pollCount >= maxPolls) {
          clearInterval(pollInterval);

          if (patentExists) {
            // Patent successfully added - refresh detections
            setSuccessMessage(`Patent ${patentId} added successfully.`);
            setTimeout(() => setSuccessMessage(null), 3000);

            if (defaultPortfolio?.portfolio_id && selectedCompPortfolioId) {
              fetchDetections(
                defaultPortfolio.portfolio_id,
                selectedCompPortfolioId,
              );
            }
          } else if (pollCount >= maxPolls) {
            // Timed out waiting for patent
            setSuccessMessage(
              `Patent ${patentId} is still processing. Refresh to check status.`,
            );
            setTimeout(() => setSuccessMessage(null), 5000);
          }
        }
      }, 2000);
    } catch (err) {
      setError(`Failed to add patent: ${err.message}`);
      setTimeout(() => setError(null), 5000);
      console.error("Error adding patent:", err);
    }
  };

  // Remove patent from portfolio (DELETE …/patent/{patentUuid})
  const removePatentFromPortfolio = async (portfolioId, patentUuid) => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/v1/portfolio/${portfolioId}/patent/${patentUuid}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Account-Id": accountId,
          },
        },
      );

      if (!res.ok) {
        const text = await res.text();
        let message = text || "Failed to remove patent";
        try {
          const parsed = JSON.parse(text);
          if (typeof parsed.message === "string") message = parsed.message;
        } catch {
          /* plain-text error body */
        }
        throw new Error(message);
      }

      const isDefault = portfolioId === defaultPortfolio?.portfolio_id;
      await fetchPortfolioPatents(portfolioId, isDefault);
      await fetchProcessingPatents(portfolioId, isDefault);

      setPatentClaimsByPatentUuid((prev) => {
        if (!prev[patentUuid]) return prev;
        const next = { ...prev };
        delete next[patentUuid];
        return next;
      });
      setExpandedDetectionId(null);
      setDetectionDetails({});

      const readerPatentId = selectedReaderPatent?.patent_id;
      if (
        readerPatentId != null &&
        String(readerPatentId) === String(patentUuid)
      ) {
        setSelectedReaderPatent(null);
        setReaderError(null);
        setHighlightedReaderClaimNumber(null);
        setReaderSourceLabel("");
      }

      // Detections + claims analysis refresh via useEffect on patent lists
      // (avoids fetchDetections with stale portfolio snapshot from this closure).
    } catch (err) {
      setError(err.message || "Failed to remove patent");
      setTimeout(() => setError(null), 5000);
      console.error("Error removing patent:", err);
    }
  };

  const renamePatentInPortfolio = async (portfolioId, patentUuid, patentName) => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/v1/portfolio/${portfolioId}/patent/${patentUuid}`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Account-Id": accountId,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ patent_name: patentName }),
        },
      );

      const raw = await res.text();
      let data = {};
      try {
        data = raw ? JSON.parse(raw) : {};
      } catch {
        /* non-JSON */
      }

      if (!res.ok) {
        throw new Error(
          typeof data.message === "string" && data.message
            ? data.message
            : raw || "Failed to update patent",
        );
      }

      const isDefault = portfolioId === defaultPortfolio?.portfolio_id;
      await fetchPortfolioPatents(portfolioId, isDefault);

      setSuccessMessage("Patent name updated.");
      setTimeout(() => setSuccessMessage(null), 4000);
    } catch (err) {
      setError(err.message || "Failed to update patent");
      setTimeout(() => setError(null), 5000);
      console.error("Error renaming patent:", err);
      throw err;
    }
  };

  const persistClaimsAnalysisScroll = () => {
    const el = claimsAnalysisScrollRef.current;
    if (el != null) {
      claimsScrollTopRestoreRef.current = el.scrollTop;
    }
  };

  const openPatentReader = async (portfolioId, patent) => {
    persistClaimsAnalysisScroll();
    setActiveTab("reader");
    setHighlightedReaderClaimNumber(null);
    setReaderLoading(true);
    setReaderError(null);
    const sourcePortfolioName =
      portfolioId === defaultPortfolio?.portfolio_id
        ? defaultPortfolio?.portfolio_name || "My Portfolio"
        : selectedCompPortfolio?.portfolio_name || "Competitor Portfolio";
    const docLabel = patent.patent_id || patent.patentUuid;
    const listName =
      patent?.patent_name != null && String(patent.patent_name).trim() !== ""
        ? String(patent.patent_name).trim()
        : null;
    setReaderSourceLabel(
      listName
        ? `${sourcePortfolioName} • ${docLabel} • ${listName}`
        : `${sourcePortfolioName} • ${docLabel}`,
    );

    try {
      const res = await fetch(
        `${API_BASE_URL}/v1/portfolio/${portfolioId}/patent/${patent.patentUuid}`,
        {
          headers: {
            "Content-Type": "application/json",
            "X-Account-Id": accountId,
            Authorization: `Bearer ${token}`,
          },
        },
      );
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || "Failed to fetch patent claims");
      }
      const fromSidebar =
        patent?.patent_name != null && String(patent.patent_name).trim() !== ""
          ? String(patent.patent_name).trim()
          : null;
      const fromApi =
        data.patent_name != null && String(data.patent_name).trim() !== ""
          ? String(data.patent_name).trim()
          : null;
      const mergedName = fromSidebar ?? fromApi ?? null;
      setSelectedReaderPatent({
        ...data,
        patent_name: mergedName,
      });
    } catch (err) {
      setReaderError(err.message || "Failed to fetch patent claims");
      setSelectedReaderPatent(null);
    } finally {
      setReaderLoading(false);
    }
  };

  const openPatentReaderAtClaim = async ({
    portfolioId,
    patentUuid,
    patentDocumentNumber,
    claimNumber,
  }) => {
    persistClaimsAnalysisScroll();
    setActiveTab("reader");
    setHighlightedReaderClaimNumber(claimNumber ? String(claimNumber) : null);
    setReaderLoading(true);
    setReaderError(null);
    const listName = getPortfolioPatentDisplayName(patentUuid);
    setReaderSourceLabel(
      listName
        ? `Claim ${claimNumber} • ${patentDocumentNumber || patentUuid} • ${listName}`
        : `Claim ${claimNumber} • ${patentDocumentNumber || patentUuid}`,
    );

    try {
      const res = await fetch(
        `${API_BASE_URL}/v1/portfolio/${portfolioId}/patent/${patentUuid}`,
        {
          headers: {
            "Content-Type": "application/json",
            "X-Account-Id": accountId,
            Authorization: `Bearer ${token}`,
          },
        },
      );
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || "Failed to fetch patent claims");
      }
      const fromList = getPortfolioPatentDisplayName(patentUuid);
      const fromApi =
        data.patent_name != null && String(data.patent_name).trim() !== ""
          ? String(data.patent_name).trim()
          : null;
      const mergedName = fromList ?? fromApi ?? null;
      setSelectedReaderPatent({
        ...data,
        patent_name: mergedName,
      });
    } catch (err) {
      setReaderError(err.message || "Failed to fetch patent claims");
      setSelectedReaderPatent(null);
    } finally {
      setReaderLoading(false);
    }
  };

  /**
   * @returns {Promise<boolean>} true if the portfolio was created
   */
  const createCompetitorPortfolio = async (portfolioName) => {
    const name = String(portfolioName || "").trim();
    if (!name) {
      setError("Enter a portfolio name");
      setTimeout(() => setError(null), 4000);
      return false;
    }

    try {
      const res = await fetch(`${API_BASE_URL}/v1/portfolio`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Account-Id": accountId,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ portfolio_name: name }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data.message || "Failed to create portfolio");
      }

      const idFromResponse =
        data.portfolio_id ?? data.portfolio?.portfolio_id ?? null;

      const { compPorts } = await fetchPortfolios();

      const newPortfolioId =
        idFromResponse ||
        [...compPorts]
          .reverse()
          .find((p) => p.portfolio_name === name)?.portfolio_id;

      if (newPortfolioId) {
        setSelectedCompPortfolioId(newPortfolioId);
        localStorage.setItem("selectedCompPortfolioId", newPortfolioId);
      }

      setSuccessMessage(`Portfolio "${name}" created.`);
      setTimeout(() => setSuccessMessage(null), 4000);
      return true;
    } catch (err) {
      setError(err.message || "Failed to create portfolio");
      setTimeout(() => setError(null), 5000);
      console.error("Error creating portfolio:", err);
      return false;
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const handleBackToAccounts = () => {
    navigate("/account");
  };

  const selectedCompPortfolio = competitorPortfolios.find(
    (p) => p.portfolio_id === selectedCompPortfolioId,
  );

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        background: "#fff",
      }}
    >
      <AccountDetailHeader
        onBackToAccounts={handleBackToAccounts}
        onLogout={handleLogout}
        user={user}
        account={account}
      />

      <AccountComparisonBar
        competitorPortfolios={competitorPortfolios}
        selectedCompPortfolioId={selectedCompPortfolioId}
        onSelectCompetitorPortfolio={setSelectedCompPortfolioId}
        onCreateCompetitorPortfolio={createCompetitorPortfolio}
      />

      <div
        style={{
          display: "flex",
          flex: 1,
          overflow: "hidden",
          background: "#fff",
        }}
      >
        <AccountPortfolioSetup
          loading={loading}
          defaultPortfolio={defaultPortfolio}
          defaultPortfolioPatents={defaultPortfolioPatents}
          defaultProcessingPatents={defaultProcessingPatents}
          selectedCompPortfolioId={selectedCompPortfolioId}
          competitorPortfolioName={selectedCompPortfolio?.portfolio_name}
          competitorPortfolioPatents={competitorPortfolioPatents}
          competitorProcessingPatents={competitorProcessingPatents}
          newMyPatentId={newMyPatentId}
          onChangeNewMyPatentId={setNewMyPatentId}
          newCompPatentId={newCompPatentId}
          onChangeNewCompPatentId={setNewCompPatentId}
          onAddPatentToPortfolio={addPatentToPortfolio}
          onRemovePatentFromPortfolio={removePatentFromPortfolio}
          onRenamePatent={renamePatentInPortfolio}
          onOpenPatentReader={openPatentReader}
        />
        <section
          style={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            background: "#fff",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: "4px",
              padding: "10px 12px 0",
              borderBottom: "1px solid #e0ddd6",
            }}
          >
            <button
              type="button"
              onClick={() => setActiveTab("claims")}
              style={{
                border: "1px solid #d4cfc4",
                borderBottom:
                  activeTab === "claims" ? "1px solid #fff" : "1px solid #d4cfc4",
                background: activeTab === "claims" ? "#fff" : "#f6f4ef",
                color: "#1e3331",
                padding: "5px 10px",
                fontSize: "11px",
                fontWeight: 600,
                cursor: "pointer",
                borderRadius: 0,
                marginBottom: "-1px",
              }}
            >
              Claims Analysis
            </button>
            <button
              type="button"
              onClick={() => {
                persistClaimsAnalysisScroll();
                setActiveTab("reader");
              }}
              style={{
                border: "1px solid #d4cfc4",
                borderBottom:
                  activeTab === "reader" ? "1px solid #fff" : "1px solid #d4cfc4",
                background: activeTab === "reader" ? "#fff" : "#f6f4ef",
                color: "#1e3331",
                padding: "5px 10px",
                fontSize: "11px",
                fontWeight: 600,
                cursor: "pointer",
                borderRadius: 0,
                marginBottom: "-1px",
              }}
            >
              Patent Reader
            </button>
          </div>
          {activeTab === "claims" ? (
            <AccountClaimsAnalysis
              defaultPortfolio={defaultPortfolio}
              selectedCompPortfolio={selectedCompPortfolio}
              detections={detections}
              loadingDetections={loadingDetections}
              defaultPortfolioPatents={defaultPortfolioPatents}
              competitorPortfolioPatents={competitorPortfolioPatents}
              selectedCompPortfolioId={selectedCompPortfolioId}
              expandedDetectionId={expandedDetectionId}
              detectionDetails={detectionDetails}
              patentClaimsByPatentUuid={patentClaimsByPatentUuid}
              onOpenClaimReference={openPatentReaderAtClaim}
              onToggleDetection={toggleDetection}
              scrollContainerRef={claimsAnalysisScrollRef}
              savedScrollTop={claimsScrollTopRestoreRef.current}
              claimComparisonVisibleCount={claimsComparisonVisibleCount}
              setClaimComparisonVisibleCount={setClaimsComparisonVisibleCount}
              collapsedClaimComparisonKeys={claimsCollapsedComparisonKeys}
              onToggleCollapsedClaimComparisonKey={(key) => {
                setClaimsCollapsedComparisonKeys((prev) => {
                  const next = new Set(prev);
                  if (next.has(key)) next.delete(key);
                  else next.add(key);
                  return next;
                });
              }}
            />
          ) : (
            <AccountPatentReader
              loading={readerLoading}
              error={readerError}
              patent={selectedReaderPatent}
              sourceLabel={readerSourceLabel}
              highlightedClaimNumber={highlightedReaderClaimNumber}
            />
          )}
        </section>
      </div>

      {/* Error Toast */}
      {error && (
        <div
          style={{
            position: "fixed",
            bottom: "20px",
            right: "20px",
            background: "#d32f2f",
            color: "white",
            padding: "12px 20px",
            borderRadius: "2px",
            border: "1px solid rgba(0,0,0,0.2)",
            boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
            zIndex: 1000,
          }}
        >
          {error}
          <button
            onClick={() => setError(null)}
            style={{
              background: "none",
              border: "none",
              color: "white",
              marginLeft: "12px",
              cursor: "pointer",
              fontSize: "16px",
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* Success Toast */}
      {successMessage && (
        <div
          style={{
            position: "fixed",
            bottom: "20px",
            right: "20px",
            background: "#2e7d32",
            color: "white",
            padding: "12px 20px",
            borderRadius: "2px",
            border: "1px solid rgba(0,0,0,0.15)",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            zIndex: 1000,
          }}
        >
          {successMessage}
          <button
            onClick={() => setSuccessMessage(null)}
            style={{
              background: "none",
              border: "none",
              color: "white",
              marginLeft: "12px",
              cursor: "pointer",
              fontSize: "16px",
            }}
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
}

export default AccountDetail;
